import React, { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  VStack,
  Skeleton,
  Button,
  Flex,
  Switch,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { UncontrolledPopover, PopoverBody } from "reactstrap";
import httpInjectorService from "services/http-injector.service";
import { IoMdOptions } from "react-icons/io";

/* DND IMPORTS */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MotionBox = motion(Box);

/* ---------------- SORTABLE ITEM ---------------- */

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width: "100%",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */

const ProjectSection = ({ data={}, loading }) => {
  /* VISIBILITY CONFIG */

  const [config, setConfig] = useState({
    totalProject: true,
    totalIssue: true,
    openBugs: true,
    overdueIssue: true,
    dueToday: true,
    sprintProgress: true,
  });

  /* ORDER STATE */

  const [metricOrder, setMetricOrder] = useState([
    "totalProject",
    "totalIssue",
    "openBugs",
    "overdueIssue",
    "dueToday",
    "sprintProgress",
  ]);

  /* METRICS DATA */
const metrics = {
  totalProject: data?.totalProject ?? null,
  totalIssue: data?.totalIssue ?? null,
  openBugs: data?.openBugs ?? null,
  overdueIssue: data?.overdueIssue ?? null,
  dueToday: data?.dueToday ?? null,
  sprintProgress: {
    percentage: data?.sprintProgress?.percentage ?? null,
  },
};

  const projectMetrics = [
    {
      key: "totalProject",
      label: "Total Project",
      count: metrics.totalProject,
      type: "number",
    },
    {
      key: "totalIssue",
      label: "Total Task",
      count: metrics.totalIssue,
      type: "number",
    },
    {
      key: "openBugs",
      label: "Open Bugs",
      count: metrics.openBugs,
      type: "number",
    },
    {
      key: "overdueIssue",
      label: "Overdue Task",
      count: metrics.overdueIssue,
      type: "number",
    },
    {
      key: "dueToday",
      label: "Due Today",
      count: metrics.dueToday,
      type: "number",
    },
    {
      key: "sprintProgress",
      label: "Sprint Progress",
      count: metrics.sprintProgress.percentage,
      type: "percentage",
    },
  ];

  /* ---------------- LOAD CONFIG ---------------- */

  const getConfig = async () => {
    try {
      const response =
        await httpInjectorService.getHorizontalDashboardConfig();

      if (
  response?.status === "success" &&
  response?.data?.length > 0 &&
  response.data[0]?.dashboard_config
) {
        const newConfig = response.data[0].dashboard_config;
        setConfig(newConfig);
      }
    } catch (error) {
      console.error("Error fetching dashboard config:", error);
    }
  };

  /* OPTIONAL — LOAD ORDER FROM BACKEND */

  const getMetricOrder = async () => {
    try {
      const response =
        await httpInjectorService.getMetricSequence();

      if (response?.data?.length > 0) {
        const sequence = response.data[0].metric_sequence;

        if (sequence) {
          setMetricOrder(sequence);
        }
      }
    } catch (error) {
      console.log("No saved sequence found");
    }
  };

  useEffect(() => {
    getConfig();
    getMetricOrder();
  }, []);

  /* ---------------- TOGGLE VISIBILITY ---------------- */

  const handleToggle = async (key) => {
    const newConfig = {
      ...config,
      [key]: !config[key],
    };

    await updateConfig(newConfig);
  };

  const updateConfig = async (newConfig) => {
    try {
      const payload = {
        dashboard_config: newConfig,
      };

      const response =
        await httpInjectorService.updateHorizontalDashboardConfig(
          payload
        );

      if (response.status === "success") {
        setConfig(newConfig);
      }
    } catch (error) {
      console.error("Error updating dashboard config:", error);
    }
  };

  /* ---------------- SAVE ORDER ---------------- */

  const saveMetricOrder = async (order) => {
    try {
      const payload = {
        metric_sequence: order,
      };

      await httpInjectorService.updateMetricSequence(
        payload
      );

     
    } catch (error) {
      console.error("Error saving order:", error);
    }
  };

  /* ---------------- DRAG HANDLER ---------------- */

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = metricOrder.indexOf(active.id);
    const newIndex = metricOrder.indexOf(over.id);

    const newOrder = arrayMove(
      metricOrder,
      oldIndex,
      newIndex
    );

    setMetricOrder(newOrder);

    await saveMetricOrder(newOrder);
  };

  /* ---------------- ORDERED + FILTERED METRICS ---------------- */

  const orderedMetrics = metricOrder
  .map((key) => {
    const metric = projectMetrics.find(
      (m) => m.key === key
    );

    if (!metric) {
      console.warn("Invalid metric key:", key);
    }

    return metric;
  })
  .filter((metric) => metric != null);

const visibleMetrics = orderedMetrics.filter(
  (metric) => config?.[metric.key]
);

  /* ---------------- UI ---------------- */

  return (
    <Box mt={8} mb={8}>
      <Flex justify="space-between" mb={4} data-tour="project-metrics">
        <h4>Project Metrics</h4>

        <Button
          colorScheme="purple"
          id="metricSettings"
          size="sm"
        >
          <IoMdOptions size="17px" />
        </Button>
      </Flex>

      {/* SETTINGS POPOVER */}

<UncontrolledPopover
  placement="right"
  target="metricSettings"
  trigger="legacy"
  container="body"
  strategy="fixed"
  popperClassName="dashboard-popover"
  modifiers={[
    {
      name: "offset",
      options: {
        offset: [120, 0],
      },
    },
  ]}
>
  <PopoverBody
    style={{
      width: "200px",
      padding: "10px 5px",
    }}
  >
          <h5
            style={{
              marginBottom: "20px",
              marginLeft: "10px",
              fontWeight: "600",
              fontSize: "18px",
            }}
          >
            Dashboard Settings
          </h5>

          {projectMetrics.map((item) => (
            <Flex
              key={item.key}
              align="center"
              justify="space-between"
              mb={2}
              px={0}
            >
              <Text fontSize="sm" fontWeight="500">
                {item.label}
              </Text>

              <Switch
                size="md"
                colorScheme="blue"
                isChecked={config[item.key]}
                onChange={() =>
                  handleToggle(item.key)
                }
              />
            </Flex>
          ))}
        </PopoverBody>
      </UncontrolledPopover>

      {/* DND SECTION */}

      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={visibleMetrics.map(
            (m) => m.key
          )}
          strategy={
            horizontalListSortingStrategy
          }
        >
          <SimpleGrid
            columns={visibleMetrics.length}
            spacing={4}
          >
            {visibleMetrics.map((metric) => (
              <SortableItem
                id={metric.key}
                key={metric.key}
              >
                {({
                  attributes,
                  listeners,
                }) => (
                  <VStack
                    spacing={0}
                    align="stretch"
                    cursor="grab"
                    {...attributes}
                    {...listeners}
                  >
                    <Text
                      fontSize="sm"
                      color="gray.600"
                      fontWeight="medium"
                    >
                      {metric.label}
                    </Text>

                    <Skeleton
                      isLoaded={!loading}
                      borderRadius="lg"
                    >
                      <MotionBox
  bg="white"
  borderRadius="lg"
  py="24px"
  px="24px"
  boxShadow="sm"
  borderWidth="1px"
  borderColor="gray.200"
  textAlign="center"
  minH="110px"
  display="flex"
  flexDirection="column"
  justifyContent="center"
  alignItems="center"
  whileHover={{
    y: -8,
    boxShadow: "0px 8px 24px rgba(0,0,0,0.12)"
  }}
  transition={{
    duration: 0.2,
    ease: "easeOut"
  }}
><Text
  fontSize="3xl"
  fontWeight="bold"
  color={
    metric.type === "percentage" &&
    metric.count !== null
      ? metric.count >= 80
        ? "green.500"
        : metric.count >= 50
        ? "yellow.500"
        : "red.500"
      : "gray.800"
  }
>
  {metric.count === null || metric.count === undefined
    ? "N/A"
    : metric.type === "percentage"
    ? `${metric.count}%`
    : metric.count}
</Text>
                      </MotionBox>
                    </Skeleton>
                  </VStack>
                )}
              </SortableItem>
            ))}
          </SimpleGrid>
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default ProjectSection;